import { useState, useEffect, useMemo, useRef } from "react"
import "../styles/playground.css"

// ---- Static config -------------------------------------------------
const ENVIRONMENTS = [
    { value: 'production', label: 'Production', host: 'https://checkout.paga.com', note: 'Live money. Real cards will be charged.' },
    { value: 'beta', label: 'Beta', host: 'https://beta-checkout.paga.com', note: 'Pre-release build. Test credentials only.' },
    { value: 'qa2', label: 'QA2', host: 'https://qa2-checkout.paga.com', note: 'QA sandbox. Resets frequently.' },
    { value: 'custom', label: 'Custom', host: '', note: 'Point at any host — e.g. a local tunnel.' }
]

// Note: there is no `redirect_url` widget parameter — only `redirect_url_method`
// is actually read (see widget-initiation-script.html's attribute whitelist and
// PaymentContext.js, which builds the redirect target from charge_url instead).
// So we don't offer a redirect URL field here; it would silently do nothing.
const DEFAULTS = {
    amount: '50.00', currency: 'NGN', email: 'ncheckout@paga.com', phone: '08123456789',
    paymentReference: '', fundingSources: 'card,paga,ussd,transfer,agent', cardScheme: 'mastercard,visa,verve',
    chargeUrl: '', redirectMethod: 'GET', buttonLabel: 'Pay',
    accountNumber: '', description: '', productCodes: '', displayName: '', displayTagline: '',
    merchantName: '', merchantLogoUrl: '', width: '', callbackUrl: ''
}

// Every `data-*` attribute the widget script (widget-initiation-script.html)
// actually reads off the <script> tag, in the order it whitelists them —
// mapping widget attribute name -> the `fields` key that supplies it. Kept as
// one list so the code snippets, the live-preview iframe and (eventually) any
// new form field all stay in lockstep with what the widget will honor.
const WIDGET_ATTRS = [
    ['charge_url', 'chargeUrl'], ['redirect_url_method', 'redirectMethod'], ['public_key', 'publicKey'],
    ['amount', 'amount'], ['currency', 'currency'], ['payment_reference', 'paymentReference'],
    ['account_number', 'accountNumber'], ['description', 'description'], ['phone_number', 'phone'],
    ['email', 'email'], ['product_codes', 'productCodes'], ['display_name', 'displayName'],
    ['display_tagline', 'displayTagline'], ['button_label', 'buttonLabel'], ['merchant_name', 'merchantName'],
    ['merchant_logo_url', 'merchantLogoUrl'], ['width', 'width'], ['funding_sources', 'fundingSources'],
    ['card_scheme', 'cardScheme'], ['callback_url', 'callbackUrl']
]

function widgetAttrs(f) {
    const out = {}
    WIDGET_ATTRS.forEach(([attr, key]) => {
        out[attr] = attr === 'button_label' ? (f.buttonLabel || 'Pay') : f[key]
    })
    return out
}

const FUNDING_CHOICES = ['card', 'paga', 'ussd', 'transfer', 'agent', 'PAYPAL']

const TEST_CARDS = [
    { scheme: 'Visa', number: '4084 0840 8408 4081', expiry: '12/29', cvv: '408', note: 'Successful — no auth' },
    { scheme: 'Mastercard', number: '5060 6666 6666 6666 666', expiry: '12/29', cvv: '123', note: 'OTP: 123456' },
    { scheme: 'Verve', number: '5061 4604 1000 0000 000', expiry: '12/29', cvv: '111', note: 'PIN: 1111 · OTP: 123456' },
    { scheme: 'Visa', number: '4000 0000 0000 0002', expiry: '12/29', cvv: '000', note: 'Declined — insufficient funds' }
]

const PREVIEW_MODES = [
    { value: 'script', label: 'Checkout script' },
    { value: 'inline', label: 'Inline JS' }
]

// ---- Custom host / recipient persistence (this browser only) --------
// Recipients you add here are merged into that environment's list from
// /demo-v2-recipients/<env>.json, on top of the JSON entries, and stick around
// (in this browser) across reloads. This blob also remembers the last
// selected environment and, per environment, the last selected recipient —
// so Step 1 and Step 2 don't reset back to their defaults on reload either.
const CUSTOM_STORAGE_KEY = 'paga-demo-v2-custom'

// ---- Variable field persistence (this browser only) ------------------
// Whatever was last typed into Step 3 sticks around across reloads, so the
// page doesn't wipe your test setup every time you come back to it.
const FIELDS_STORAGE_KEY = 'paga-demo-v2-fields'

function loadSavedFields() {
    try {
        const raw = localStorage.getItem(FIELDS_STORAGE_KEY)
        return raw ? JSON.parse(raw) : {}
    } catch (err) { return {} }
}

function saveFields(fields) {
    try {
        // publicKey is derived from the selected recipient, not a "variable" —
        // don't let a stale saved key fight with recipient selection on reload.
        const { publicKey, ...rest } = fields
        localStorage.setItem(FIELDS_STORAGE_KEY, JSON.stringify(rest))
    } catch (err) { /* localStorage unavailable — ignore, nothing persists this session */ }
}

// Whether the setup sidebar is collapsed to a thin rail — sticks across reloads too.
const SIDEBAR_STORAGE_KEY = 'paga-demo-v2-sidebar-collapsed'

function loadSidebarCollapsed() {
    try { return localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1' } catch (err) { return false }
}

function saveSidebarCollapsed(collapsed) {
    try { localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? '1' : '0') } catch (err) { /* ignore */ }
}

function loadCustom() {
    try {
        const raw = localStorage.getItem(CUSTOM_STORAGE_KEY)
        return raw ? JSON.parse(raw) : {}
    } catch (err) { return {} }
}

function saveCustom(patch) {
    try {
        const current = loadCustom()
        localStorage.setItem(CUSTOM_STORAGE_KEY, JSON.stringify(Object.assign({}, current, patch)))
    } catch (err) { /* localStorage unavailable — ignore, nothing persists this session */ }
}

function getCustomRecipients(env) {
    return (loadCustom().recipients || {})[env] || []
}

function addCustomRecipient(env, recipient) {
    const custom = loadCustom()
    const recipients = Object.assign({}, custom.recipients)
    recipients[env] = (recipients[env] || []).concat([recipient])
    saveCustom({ recipients })
}

function removeCustomRecipient(env, publicKey) {
    const custom = loadCustom()
    const recipients = Object.assign({}, custom.recipients)
    recipients[env] = (recipients[env] || []).filter(r => r.publicKey !== publicKey)
    saveCustom({ recipients })
}

function loadLastEnv() {
    return loadCustom().env || ''
}

function saveLastEnv(env) {
    saveCustom({ env })
}

function loadLastRecipient(env) {
    return (loadCustom().lastRecipient || {})[env] || ''
}

function saveLastRecipient(env, publicKey) {
    const custom = loadCustom()
    const lastRecipient = Object.assign({}, custom.lastRecipient)
    lastRecipient[env] = publicKey
    saveCustom({ lastRecipient })
}

function envObj(env) {
    return ENVIRONMENTS.find(e => e.value === env) || ENVIRONMENTS[0]
}

function hostFor(env, customHost) {
    const e = envObj(env)
    return e.value === 'custom' ? (customHost || '').replace(/\/+$/, '') : e.host
}

function escapeHtmlAttr(v) {
    return String(v == null ? '' : v)
        .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
        .replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// ---- Code / iframe-doc builders (pure functions of state) -----------
function scriptSnippet(base, f) {
    const pad = '        '
    const attrs = widgetAttrs(f)
    const rows = Object.keys(attrs).map(k => pad + 'data-' + k + '="' + (attrs[k] == null ? '' : attrs[k]) + '"')
    const open = '<' + 'script'
    return open + ' src="' + base + '/checkout/?w=280&h=50"\n' + pad + 'id="checkout-script"\n' + rows.join('\n') + '>\n</' + 'script>'
}

function inlineSnippet(base, f) {
    const q = v => '"' + (v == null ? '' : v) + '"'
    const open = '<' + 'script'
    const close = '</' + 'script>'
    return open + ' id="js-script" src="' + base + '/checkout/inline-js">' + close + '\n\n' +
        '<form id="payment-form">\n' +
        '  <button type="button" class="checkout-button" onclick="startPayment()">' + (f.buttonLabel || 'Pay') + '</button>\n' +
        '</form>\n\n' +
        open + '>\n' +
        '  function startPayment() {\n' +
        '    PagaCheckout.setOptions({\n' +
        '      publicKey: ' + q(f.publicKey) + ',\n' +
        '      amount: ' + q(f.amount) + ',\n' +
        '      currency: ' + q(f.currency) + ',\n' +
        '      paymentReference: ' + q(f.paymentReference) + ',\n' +
        '      email: ' + q(f.email) + ',\n' +
        '      phoneNumber: ' + q(f.phone) + ',\n' +
        '      fundingSources: ' + q(f.fundingSources) + ',\n' +
        '      cardScheme: ' + q(f.cardScheme) + ',\n' +
        '      additional_data: {\n' +
        '        product_name: "",\n' +
        '        product_category: ""\n' +
        '      },\n' +
        '      chargeUrl: ' + q(f.chargeUrl) + '\n' +
        '    });\n\n' +
        '    PagaCheckout.openCheckout();\n' +
        '  }\n' +
        close
}

// Both the checkout-script widget and the inline-js bundle are written to run once,
// straight off the initial page parse (that's how every static demo page uses them) —
// the widget waits on `document.addEventListener('DOMContentLoaded', ...)`, which never
// fires again once the page has already loaded, and the inline-js bundle declares
// `const hostname` / `class PagaCheckout` at top level, which throws
// "Identifier has already been declared" if the same script runs twice in one document.
// So rather than injecting <script> tags into this page directly, each preview gets its
// own sandboxed iframe with fresh markup — same trick the static demo pages get for free
// by being a full page load, just scoped to a widget. Reloading = rewriting the iframe's
// srcDoc, which always starts a brand new document (fresh globals, fresh DOMContentLoaded).
//
// The checkout iframe the widget/inline-js opens is `position:fixed`, which is scoped to
// whichever document it lives in — so hosting it inside a big #preview-frame here makes it
// fill that frame like a real subpage, instead of covering the whole outer page.
function scriptFrameDoc(base, f) {
    const attrs = widgetAttrs(f)
    const attrHtml = Object.keys(attrs).map(k => 'data-' + k + '="' + escapeHtmlAttr(attrs[k]) + '"').join('\n            ')
    return '<!DOCTYPE html><html><head><meta charset="utf-8">' +
        '<style>body{margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:sans-serif;}</style>' +
        '</head><body>' +
        '<script id="checkout-script" src="' + base + '/checkout/?w=280&h=50"\n            ' + attrHtml + '><' + '/script>' +
        '<script>window.addEventListener("load", function () { parent.postMessage({ dv2: true, kind: "status", status: "loaded" }, "*") });<' + '/script>' +
        '</body></html>'
}

function inlineFrameDoc(base, f) {
    const q = v => JSON.stringify(v == null ? '' : v)
    return '<!DOCTYPE html><html><head><meta charset="utf-8">' +
        '<style>body{margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;}' +
        '.checkout-button{background:#F36C36;font-size:14px;font-weight:bold;color:#fff;border-radius:20px;height:40px;border:none;padding:0 20px;cursor:pointer;font-family:sans-serif;}</style>' +
        '</head><body>' +
        '<form id="payment-form">' +
        '<button type="button" class="checkout-button" id="pay-btn">' + escapeHtmlAttr(f.buttonLabel || 'Pay') + '</button>' +
        '</form>' +
        '<script id="js-script" src="' + base + '/checkout/inline-js"><' + '/script>' +
        '<script>' +
        'window.addEventListener("load", function () {' +
        '  var ready = (typeof PagaCheckout !== "undefined");' +
        '  parent.postMessage({ dv2: true, kind: "status", status: ready ? "ready" : "no global" }, "*");' +
        '});' +
        'document.getElementById("pay-btn").addEventListener("click", function () {' +
        '  if (typeof PagaCheckout === "undefined") {' +
        '    parent.postMessage({ dv2: true, kind: "message", text: "PagaCheckout isn\'t loaded — hit Reload scripts, or check ' + base + '/checkout/inline-js." }, "*");' +
        '    return;' +
        '  }' +
        '  try {' +
        '    PagaCheckout.setOptions({' +
        '      publicKey: ' + q(f.publicKey) + ',' +
        '      amount: ' + q(f.amount) + ',' +
        '      currency: ' + q(f.currency) + ',' +
        '      paymentReference: ' + q(f.paymentReference) + ',' +
        '      email: ' + q(f.email) + ',' +
        '      phoneNumber: ' + q(f.phone) + ',' +
        '      fundingSources: ' + q(f.fundingSources) + ',' +
        '      cardScheme: ' + q(f.cardScheme) + ',' +
        '      additional_data: { product_name: "", product_category: "" },' +
        '      chargeUrl: ' + q(f.chargeUrl) +
        '    });' +
        '    PagaCheckout.openCheckout();' +
        '    parent.postMessage({ dv2: true, kind: "message", text: "openCheckout() called on ' + base + '." }, "*");' +
        '  } catch (err) {' +
        '    parent.postMessage({ dv2: true, kind: "message", text: "openCheckout() threw: " + (err && err.message ? err.message : String(err)) }, "*");' +
        '  }' +
        '});' +
        '<' + '/script>' +
        '</body></html>'
}

// "custom" is only offered when the page is opened with ?mode=internal
function visibleEnvs() {
    const searchParams = new URLSearchParams(window.location.search)
    const isInternalMode = searchParams.get('mode') === 'internal'
    return ENVIRONMENTS.filter(e => e.value !== 'custom' || isInternalMode)
}

const Playground = () => {
    const VISIBLE_ENVS = useMemo(visibleEnvs, [])
    const isMobile = useMemo(() => window.matchMedia('(max-width: 900px)').matches, [])

    const [env, setEnvValue] = useState(() => {
        const saved = loadLastEnv()
        // Ignore a saved env this page isn't currently offering (e.g. "custom"
        // saved while in ?mode=internal, reloaded without it).
        return VISIBLE_ENVS.some(e => e.value === saved) ? saved : VISIBLE_ENVS[0].value
    })
    const [customHost, setCustomHost] = useState(loadCustom().host || '')
    const [recipients, setRecipients] = useState([])
    const [recipientIndex, setRecipientIndex] = useState(0) // number, or 'add'
    const [newRecipientName, setNewRecipientName] = useState('')
    const [fields, setFields] = useState(Object.assign({}, DEFAULTS, loadSavedFields(), { publicKey: '' }))
    const [previewMode, setPreviewMode] = useState('script') // 'script' | 'inline' — the one script/inline switch, lives in the sidebar
    const [varsOpen, setVarsOpen] = useState(!isMobile)
    const [showMoreVars, setShowMoreVars] = useState(false)
    const [codeOpen, setCodeOpen] = useState(false)
    const [testCardsOpen, setTestCardsOpen] = useState(false)
    const [configOpen, setConfigOpen] = useState(!isMobile)
    const [sidebarCollapsed, setSidebarCollapsed] = useState(loadSidebarCollapsed)
    const [previewStatus, setPreviewStatus] = useState('idle')
    const [previewMessage, setPreviewMessage] = useState('')
    const [previewDoc, setPreviewDoc] = useState('')
    // Bumped on every explicit "Reload scripts" click and used as the iframe's `key`,
    // so the frame always remounts — setting `srcDoc` to a string identical to what's
    // already there (nothing in the form changed) wouldn't otherwise reload anything.
    const [iframeKey, setIframeKey] = useState(0)
    const [copyLabel, setCopyLabel] = useState('Copy code')

    const base = hostFor(env, customHost)

    function updateField(key, value) {
        setFields(prev => Object.assign({}, prev, { [key]: value }))
    }

    // Persist whatever's typed into Step 3 so it survives a reload — but not
    // publicKey, which is derived from the selected recipient (see saveFields).
    useEffect(() => { saveFields(fields) }, [fields])

    function toggleSidebar() {
        setSidebarCollapsed(prev => {
            const next = !prev
            saveSidebarCollapsed(next)
            return next
        })
    }

    // Selects, in order of preference: the recipient last selected for this
    // environment (if it's still in the freshly loaded list), else the first
    // recipient in that list (env's JSON list + this browser's saved custom
    // recipients), else drops into "add" mode if there are none at all yet.
    function selectFirstRecipient(list) {
        const savedKey = loadLastRecipient(env)
        const savedIdx = savedKey ? list.findIndex(r => r.publicKey === savedKey) : -1
        if (savedIdx >= 0) {
            setRecipientIndex(savedIdx)
            updateField('publicKey', list[savedIdx].publicKey || '')
        } else if (list.length) {
            setRecipientIndex(0)
            updateField('publicKey', list[0].publicKey || '')
            saveLastRecipient(env, list[0].publicKey || '')
        } else {
            setRecipientIndex('add')
            updateField('publicKey', '')
        }
        setNewRecipientName('')
    }

    // Load recipients (env's JSON list + this browser's custom ones) whenever
    // the environment changes.
    useEffect(() => {
        let cancelled = false
        const customList = getCustomRecipients(env).map(r => Object.assign({}, r, { custom: true }))
        if (env === 'custom') {
            // No JSON file for the custom environment — just whatever's been saved for it.
            setRecipients(customList)
            selectFirstRecipient(customList)
            return
        }
        fetch('/demo-v2-recipients/' + env + '.json')
            .then(r => r.ok ? r.json() : { recipients: [] })
            .catch(() => ({ recipients: [] }))
            .then(data => {
                if (cancelled) return
                const jsonList = (data.recipients || []).map(r => Object.assign({}, r, { custom: false }))
                const list = jsonList.concat(customList)
                setRecipients(list)
                selectFirstRecipient(list)
            })
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [env])

    function setEnv(v) {
        setEnvValue(v)
        saveLastEnv(v)
        const isCustomEnv = envObj(v).value === 'custom'
        // Restore the last host typed for the custom environment rather than blanking it.
        setCustomHost(isCustomEnv ? (loadCustom().host || '') : '')
    }

    function onCustomHostChange(v) {
        setCustomHost(v)
        saveCustom({ host: v })
    }

    function onRecipientSelect(v) {
        const idx = v === 'add' ? 'add' : Number(v)
        setRecipientIndex(idx)
        if (idx === 'add') {
            updateField('publicKey', '')
            setNewRecipientName('')
        } else {
            const publicKey = (recipients[idx] || {}).publicKey || ''
            updateField('publicKey', publicKey)
            saveLastRecipient(env, publicKey)
        }
    }

    function saveNewRecipient() {
        const publicKey = (fields.publicKey || '').trim()
        if (!publicKey) {
            setPreviewMessage('Enter a public key before saving this recipient.')
            return
        }
        const recipient = { name: newRecipientName.trim() || 'Custom recipient', publicKey, custom: true }
        addCustomRecipient(env, { name: recipient.name, publicKey: recipient.publicKey })
        const next = recipients.concat([recipient])
        setRecipients(next)
        setRecipientIndex(next.length - 1)
        setNewRecipientName('')
        saveLastRecipient(env, publicKey)
    }

    function removeSelectedRecipient() {
        const current = recipients[recipientIndex]
        if (!current || !current.custom) return
        removeCustomRecipient(env, current.publicKey)
        const next = recipients.filter(r => r !== current)
        setRecipients(next)
        selectFirstRecipient(next)
    }

    function toggleFundingSource(token) {
        const list = (fields.fundingSources || '').split(',').map(x => x.trim()).filter(Boolean)
        const i = list.map(x => x.toLowerCase()).indexOf(token.toLowerCase())
        if (i >= 0) list.splice(i, 1); else list.push(token)
        updateField('fundingSources', list.join(','))
    }

    function resetFields() {
        setFields(Object.assign({}, DEFAULTS, { publicKey: (recipients[recipientIndex] || {}).publicKey || '' }))
    }

    // ---- Code snippets & preview doc: pure derivations of state --------
    // Because these are computed straight from state on every render, they can
    // never go stale the way hand-wired imperative re-render calls could.
    const currentScriptSnippet = useMemo(() => scriptSnippet(base || 'https://checkout.paga.com', fields), [base, fields])
    const currentInlineSnippet = useMemo(() => inlineSnippet(base || 'https://checkout.paga.com', fields), [base, fields])
    const codeText = previewMode === 'script' ? currentScriptSnippet : currentInlineSnippet

    // Auto-reload the live preview whenever anything it depends on changes —
    // debounced slightly so a burst of keystrokes doesn't thrash the iframe.
    const reloadNonce = useRef(0)
    useEffect(() => {
        if (!base) {
            setPreviewStatus('no host')
            setPreviewDoc('')
            return
        }
        setPreviewStatus('loading')
        const myNonce = ++reloadNonce.current
        const timer = setTimeout(() => {
            if (myNonce !== reloadNonce.current) return
            setPreviewDoc(previewMode === 'script' ? scriptFrameDoc(base, fields) : inlineFrameDoc(base, fields))
        }, 300)
        return () => clearTimeout(timer)
    }, [base, fields, previewMode])

    useEffect(() => {
        function onMessage(e) {
            const data = e.data
            if (!data || !data.dv2) return
            if (data.kind === 'status') setPreviewStatus(data.status)
            if (data.kind === 'message') setPreviewMessage(data.text)
        }
        window.addEventListener('message', onMessage)
        return () => window.removeEventListener('message', onMessage)
    }, [])

    function reloadScripts() {
        if (!base) return
        setPreviewStatus('loading')
        setPreviewDoc(previewMode === 'script' ? scriptFrameDoc(base, fields) : inlineFrameDoc(base, fields))
        setIframeKey(k => k + 1)
    }

    function copyCode() {
        const text = codeText
        const done = () => {
            setCopyLabel('Copied')
            setTimeout(() => setCopyLabel('Copy code'), 1600)
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(done, done)
        } else {
            const ta = document.createElement('textarea')
            ta.value = text
            document.body.appendChild(ta)
            ta.select()
            try { document.execCommand('copy') } catch (err) { }
            ta.remove()
            done()
        }
    }

    const current = recipientIndex === 'add' ? null : recipients[recipientIndex]
    const statusClass = 'dv2-status-pill ' + previewStatus.replace(/\s+/g, '-').toLowerCase()

    return (
        <>
            <header className="dv2-header">
                <div className="dv2-brand">
                    <img src="/favicon.ico" alt="Paga" />
                    <div>
                        <div className="dv2-brand-title">Checkout playground</div>
                        <div className="dv2-brand-sub">Try Paga Checkout across environments</div>
                    </div>
                </div>
                <div className="dv2-powered">
                    {env === 'production' && (
                        <span className="dv2-live-badge">
                            <span className="dv2-live-badge-dot"></span>
                            Live — real money
                        </span>
                    )}
                    <span>demo-v2</span>
                </div>
            </header>

            <div className={'dv2-layout' + (sidebarCollapsed ? ' dv2-layout-collapsed' : '')}>

                {/* Left column: setup side nav (collapsible to a thin rail on desktop,
                    an accordion on mobile). */}
                <div className={'dv2-config-col' + (sidebarCollapsed ? ' dv2-config-col-collapsed' : '')}>

                    <button type="button" className="dv2-sidebar-toggle" onClick={toggleSidebar} aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                        <span dangerouslySetInnerHTML={{ __html: sidebarCollapsed ? '&#8250;' : '&#8249;' }} />
                    </button>

                    <button type="button" className="dv2-config-toggle" onClick={() => setConfigOpen(!configOpen)}>
                        <span>Configuration</span>
                        <span className="dv2-chevron" dangerouslySetInnerHTML={{ __html: configOpen ? '&#9662;' : '&#9656;' }} />
                    </button>

                    <div className="dv2-config-body" style={{ display: configOpen ? 'flex' : 'none', flexDirection: 'column', gap: 20 }}>

                        {/* Preview mode: which integration the display side renders */}
                        <section className="dv2-card">
                            <div>
                                <div className="dv2-eyebrow">Preview mode</div>
                                <h2 className="dv2-h2">How you're integrating</h2>
                            </div>
                            <div className="dv2-tabs">
                                {PREVIEW_MODES.map(m => (
                                    <button key={m.value} type="button" className={'dv2-tab' + (previewMode === m.value ? ' active' : '')} onClick={() => setPreviewMode(m.value)}>{m.label}</button>
                                ))}
                            </div>
                            <div className="dv2-note">Drives both the live checkout on the right and the "Checkout script"/"Inline JS" code tab.</div>
                        </section>

                        {/* Step 1: Environment */}
                        <section className="dv2-card">
                            <div>
                                <div className="dv2-eyebrow">Step 1</div>
                                <h2 className="dv2-h2">Environment</h2>
                            </div>

                            <div className="dv2-tabs">
                                {VISIBLE_ENVS.map(e => (
                                    <button key={e.value} type="button"
                                        className={'dv2-tab' + (e.value === 'production' ? ' dv2-tab-danger' : '') + (env === e.value ? ' active' : '')}
                                        onClick={() => setEnv(e.value)}>
                                        {e.label}
                                    </button>
                                ))}
                            </div>

                            {env === 'production' ? (
                                <div className="dv2-warning-banner">
                                    <span className="dv2-warning-banner-icon">⚠️</span>
                                    <span>You're on Production. This is live money — real cards will be charged.</span>
                                </div>
                            ) : (
                                <div className="dv2-note">{envObj(env).note}</div>
                            )}

                            {envObj(env).value === 'custom' && (
                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="custom-host">Custom checkout host</label>
                                    <input className="dv2-input" id="custom-host" type="text" placeholder="https://checkout-paga-aman.ngrok.io"
                                        value={customHost} onChange={e => onCustomHostChange(e.target.value)} />
                                    <span className="dv2-helper">No trailing slash — script paths are appended for you.</span>
                                </div>
                            )}

                            <div className="dv2-kv">
                                <span className="dv2-kv-label">Resolved base</span>
                                <code className="dv2-kv-value">{base || '— enter a custom host —'}</code>
                            </div>
                        </section>

                        {/* Step 2: Recipient */}
                        <section className="dv2-card">
                            <div>
                                <div className="dv2-eyebrow">Step 2</div>
                                <h2 className="dv2-h2">Who gets paid</h2>
                            </div>

                            <div className="dv2-field">
                                <label className="dv2-label" htmlFor="recipient-select">Recipient</label>
                                <select className="dv2-select" id="recipient-select" value={recipientIndex === 'add' ? 'add' : String(recipientIndex)} onChange={e => onRecipientSelect(e.target.value)}>
                                    {recipients.map((r, i) => (
                                        <option key={i} value={String(i)}>{r.name + (r.custom ? ' (custom)' : '')}</option>
                                    ))}
                                    <option value="add">+ Add custom recipient</option>
                                </select>
                            </div>

                            {recipientIndex === 'add' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    <div className="dv2-field">
                                        <label className="dv2-label" htmlFor="f-recipient-name-custom">Name</label>
                                        <input className="dv2-input" id="f-recipient-name-custom" type="text" placeholder="e.g. My test merchant"
                                            value={newRecipientName} onChange={e => setNewRecipientName(e.target.value)} />
                                    </div>
                                    <div className="dv2-field">
                                        <label className="dv2-label" htmlFor="f-public-key-custom">Public key</label>
                                        <input className="dv2-input" id="f-public-key-custom" type="text" placeholder="Merchant public key"
                                            value={fields.publicKey} onChange={e => updateField('publicKey', e.target.value)} />
                                    </div>
                                    <button type="button" className="dv2-btn dv2-btn-primary dv2-btn-sm" style={{ alignSelf: 'flex-start' }} onClick={saveNewRecipient}>Save recipient</button>
                                    <span className="dv2-helper">Saved to this browser's local storage — merged into the list above.</span>
                                </div>
                            )}

                            {recipientIndex !== 'add' && (
                                <div className="dv2-kv">
                                    <span className="dv2-kv-label">Public key</span>
                                    <code className="dv2-kv-value">{fields.publicKey || '—'}</code>
                                </div>
                            )}

                            {current && current.custom && (
                                <button type="button" className="dv2-btn dv2-btn-ghost dv2-btn-sm" style={{ alignSelf: 'flex-start' }} onClick={removeSelectedRecipient}>Remove this recipient</button>
                            )}
                        </section>

                        {/* Step 3: Variables */}
                        <section className="dv2-card">
                            <div className="dv2-section-toggle">
                                <div className="dv2-section-toggle-left" onClick={() => setVarsOpen(!varsOpen)}>
                                    <span className="dv2-chevron" dangerouslySetInnerHTML={{ __html: varsOpen ? '&#9662;' : '&#9656;' }} />
                                    <div>
                                        <div className="dv2-eyebrow">Step 3</div>
                                        <h2 className="dv2-h2">Variables</h2>
                                    </div>
                                </div>
                                <button type="button" className="dv2-btn dv2-btn-ghost" onClick={resetFields}>Reset</button>
                            </div>

                            <div className="dv2-grid2" style={{ display: varsOpen ? 'grid' : 'none' }}>
                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-amount">Amount</label>
                                    <input className="dv2-input" id="f-amount" value={fields.amount} onChange={e => updateField('amount', e.target.value)} />
                                </div>
                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-currency">Currency</label>
                                    <select className="dv2-select" id="f-currency" value={fields.currency} onChange={e => updateField('currency', e.target.value)}>
                                        <option>NGN</option>
                                        <option>USD</option>
                                        <option>GBP</option>
                                        <option>EUR</option>
                                    </select>
                                </div>
                                <div className="dv2-field dv2-span2">
                                    <label className="dv2-label" htmlFor="f-email">Email</label>
                                    <input className="dv2-input" id="f-email" value={fields.email} onChange={e => updateField('email', e.target.value)} />
                                </div>
                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-phone">Phone number</label>
                                    <input className="dv2-input" id="f-phone" value={fields.phone} onChange={e => updateField('phone', e.target.value)} />
                                </div>
                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-button-label">Button label</label>
                                    <input className="dv2-input" id="f-button-label" value={fields.buttonLabel} onChange={e => updateField('buttonLabel', e.target.value)} />
                                </div>

                                <div className="dv2-field dv2-span2">
                                    <span className="dv2-label">Funding sources</span>
                                    <div className="dv2-chip-row">
                                        {FUNDING_CHOICES.map(token => {
                                            const active = (fields.fundingSources || '').split(',').map(x => x.trim().toLowerCase()).indexOf(token.toLowerCase()) !== -1
                                            return (
                                                <button key={token} type="button" className={'dv2-chip' + (active ? ' active' : '')} onClick={() => toggleFundingSource(token)}>{token}</button>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="dv2-btn dv2-btn-ghost dv2-btn-sm" style={{ display: varsOpen ? 'inline-flex' : 'none', alignSelf: 'flex-start' }} onClick={() => setShowMoreVars(!showMoreVars)}>
                                {showMoreVars ? 'Show fewer variables' : 'Show more variables'}
                            </button>

                            <div className="dv2-grid2" style={{ display: (varsOpen && showMoreVars) ? 'grid' : 'none' }}>
                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-payment-reference">Payment reference</label>
                                    <input className="dv2-input" id="f-payment-reference" placeholder="auto if blank" value={fields.paymentReference} onChange={e => updateField('paymentReference', e.target.value)} />
                                </div>

                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-card-scheme">Card schemes</label>
                                    <select className="dv2-select" id="f-card-scheme" value={fields.cardScheme} onChange={e => updateField('cardScheme', e.target.value)}>
                                        <option value="mastercard,visa,verve">mastercard,visa,verve</option>
                                        <option value="mastercard,visa">mastercard,visa</option>
                                        <option value="verve">verve</option>
                                    </select>
                                </div>

                                <div className="dv2-field dv2-span2">
                                    <label className="dv2-label" htmlFor="f-charge-url">Charge URL</label>
                                    <input className="dv2-input" id="f-charge-url" placeholder="optional webhook" value={fields.chargeUrl} onChange={e => updateField('chargeUrl', e.target.value)} />
                                </div>
                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-redirect-method">Redirect method</label>
                                    <select className="dv2-select" id="f-redirect-method" value={fields.redirectMethod} onChange={e => updateField('redirectMethod', e.target.value)}>
                                        <option>GET</option>
                                        <option>POST</option>
                                    </select>
                                </div>

                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-account-number">Account number</label>
                                    <input className="dv2-input" id="f-account-number" placeholder="optional" value={fields.accountNumber} onChange={e => updateField('accountNumber', e.target.value)} />
                                </div>
                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-width">Width</label>
                                    <input className="dv2-input" id="f-width" placeholder="optional" value={fields.width} onChange={e => updateField('width', e.target.value)} />
                                </div>

                                <div className="dv2-field dv2-span2">
                                    <label className="dv2-label" htmlFor="f-description">Description</label>
                                    <input className="dv2-input" id="f-description" placeholder="optional" value={fields.description} onChange={e => updateField('description', e.target.value)} />
                                </div>

                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-product-codes">Product codes</label>
                                    <input className="dv2-input" id="f-product-codes" placeholder="optional" value={fields.productCodes} onChange={e => updateField('productCodes', e.target.value)} />
                                </div>
                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-callback-url">Callback URL</label>
                                    <input className="dv2-input" id="f-callback-url" placeholder="optional" value={fields.callbackUrl} onChange={e => updateField('callbackUrl', e.target.value)} />
                                </div>

                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-display-name">Display name</label>
                                    <input className="dv2-input" id="f-display-name" placeholder="optional" value={fields.displayName} onChange={e => updateField('displayName', e.target.value)} />
                                </div>
                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-display-tagline">Display tagline</label>
                                    <input className="dv2-input" id="f-display-tagline" placeholder="optional" value={fields.displayTagline} onChange={e => updateField('displayTagline', e.target.value)} />
                                </div>

                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-merchant-name">Merchant name</label>
                                    <input className="dv2-input" id="f-merchant-name" placeholder="optional" value={fields.merchantName} onChange={e => updateField('merchantName', e.target.value)} />
                                </div>
                                <div className="dv2-field">
                                    <label className="dv2-label" htmlFor="f-merchant-logo-url">Merchant logo URL</label>
                                    <input className="dv2-input" id="f-merchant-logo-url" placeholder="optional" value={fields.merchantLogoUrl} onChange={e => updateField('merchantLogoUrl', e.target.value)} />
                                </div>
                            </div>
                        </section>

                    </div>
                </div>

                {/* Right side: the display page — everything but setup, filling the
                    rest of the viewport. Code + test cards live in floating panels
                    (below) so this stays 100% dedicated to the live checkout. */}
                <div className="dv2-preview-col">
                    <div className="dv2-preview-col-body">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                                <div>
                                    <h2 className="dv2-rail-h2">Live checkout</h2>
                                    <div className="dv2-rail-note">The real checkout subpage, loaded from the selected environment — {envObj(env).label} / {PREVIEW_MODES.find(m => m.value === previewMode).label}.</div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <span className={statusClass}>{previewStatus}</span>
                                    <button type="button" className="dv2-btn dv2-btn-primary dv2-btn-sm" onClick={reloadScripts}>Reload scripts</button>
                                </div>
                            </div>

                            {env === 'production' && (
                                <div className="dv2-warning-banner">
                                    <span className="dv2-warning-banner-icon">⚠️</span>
                                    <span>Production environment selected — this checkout will move real money.</span>
                                </div>
                            )}

                            <iframe key={iframeKey} title="Checkout preview" sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-storage-access-by-user-activation allow-popups-to-escape-sandbox"
                                className="dv2-preview-frame dv2-preview-frame-fill" srcDoc={previewDoc}></iframe>

                            {previewMessage && (
                                <div className="dv2-message dv2-message-dark">
                                    <span className="dv2-message-dot"></span>
                                    <span>{previewMessage}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating action buttons: integration code + test-card help, stacked bottom-right */}
            <div className="dv2-fab-stack">
                <button type="button" className="dv2-fab" aria-label="Integration code" title="Integration code" onClick={() => { setCodeOpen(!codeOpen); setTestCardsOpen(false) }}>
                    {'</>'}
                </button>
                <button type="button" className="dv2-fab" aria-label="Test cards" title="Test cards" onClick={() => { setTestCardsOpen(!testCardsOpen); setCodeOpen(false) }}>
                    ?
                </button>
            </div>

            {codeOpen && (
                <section className="dv2-fab-panel dv2-code-card">
                    <div className="dv2-code-head">
                        <div>
                            <h2 className="dv2-code-title">Integration code — {PREVIEW_MODES.find(m => m.value === previewMode).label}</h2>
                            <div className="dv2-code-sub">Reflects your settings on the left, ready to paste. Switch script/inline over there too.</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button type="button" className="dv2-btn dv2-btn-primary dv2-btn-sm" onClick={copyCode}>{copyLabel}</button>
                            <button type="button" className="dv2-fab-panel-close" aria-label="Close" onClick={() => setCodeOpen(false)}>&#10005;</button>
                        </div>
                    </div>
                    <pre className="dv2-code-pre">{codeText}</pre>
                </section>
            )}

            {testCardsOpen && (
                <section className="dv2-fab-panel dv2-card">
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                        <h2 className="dv2-h2" style={{ fontSize: 17 }}>Test cards</h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span className="dv2-note">Non-production environments only</span>
                            <button type="button" className="dv2-fab-panel-close" aria-label="Close" onClick={() => setTestCardsOpen(false)}>&#10005;</button>
                        </div>
                    </div>
                    <div className="dv2-table-head">
                        <span>Scheme</span><span>Number</span><span>Expiry</span><span>CVV</span><span>Result</span>
                    </div>
                    <div>
                        {TEST_CARDS.map((c, i) => (
                            <div key={i} className="dv2-table-row">
                                <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{c.scheme}</span>
                                <code>{c.number}</code>
                                <span>{c.expiry}</span>
                                <span>{c.cvv}</span>
                                <span style={{ color: 'var(--text-muted)' }}>{c.note}</span>
                            </div>
                        ))}
                    </div>
                    <div className="dv2-note" style={{ marginTop: 10 }}>
                        Paying with PayPal? Use PayPal's own{' '}
                        <a href="https://developer.paypal.com/sandbox-testing/card-testing/#test-generated-card-numbers" target="_blank" rel="noreferrer">sandbox test cards</a>{' '}
                        instead of the ones above.
                    </div>
                </section>
            )}
        </>
    )
}

export default Playground
